/**
 * <SkeletonGroup> — le moteur du balayage, un par page (atom).
 *
 * UNE valeur animée et UNE boucle pour toute la page : chaque `<Skeleton>` monté dessous lit la
 * même progression et n'en dérive que sa position. Trente blocs ne font donc pas trente
 * animations, et le trait est le même partout : il part du bord gauche de la page et la traverse
 * à vitesse constante, passant sur chaque bloc à son tour — une page lue de gauche à droite, pas
 * trente éclats en même temps.
 *
 * `<PageTemplate skeleton>` le pose lui-même autour du squelette d'une page : une page n'a pas à
 * y penser. Une story d'atome le pose à la main.
 *
 * Mouvement réduit (`prefers-reduced-motion`, « Réduire les animations ») : aucune boucle, les
 * blocs restent fixes. La nuit se lit ici, une fois pour tous les blocs : la bande n'y a ni la
 * même forme ni le même poids (`skeletonSweep`).
 */
import React, { createContext, useEffect, useMemo, useRef, useState } from "react"
import { Animated, Easing, Platform, View, type LayoutChangeEvent, type StyleProp, type ViewStyle } from "react-native"
import { useUnistyles } from "react-native-unistyles"

import { useIsDarkTheme } from "../../hooks/useIsDarkTheme"
import { useReduceMotion } from "../../hooks/useReduceMotion"
import { skeletonSweep, sweepLength } from "./skeletonSweep"

/** Ce qu'un bloc lit du moteur de sa page. */
export type SkeletonSweep = {
    /** 0 → 1 à chaque tour : le trait entre par la gauche, sort par la droite et file hors champ. */
    progress: Animated.Value
    /** La vue de la page, contre laquelle chaque bloc mesure sa position. */
    host: React.RefObject<View | null>
    /** Largeur de la page, en px — 0 tant qu'elle n'est pas mesurée. */
    width: number
    /** Longueur du trait, en px. */
    length: number
    /** Sur la nuit, la bande couvre la barre entière, et elle est blanche (`skeleton-sweep`). */
    night: boolean
    /** `false` en mouvement réduit : les blocs restent fixes. */
    moving: boolean
}

export const SkeletonSweepContext = createContext<SkeletonSweep | null>(null)

export type SkeletonGroupProps = {
    children: React.ReactNode
    /** Ce que lit un lecteur d'écran à la place des blocs — « Chargement » par défaut. */
    accessibilityLabel?: string
    style?: StyleProp<ViewStyle>
    testID?: string
}

export function SkeletonGroup(props: SkeletonGroupProps) {
    const { children, accessibilityLabel = "Chargement", style, testID } = props
    const reduceMotion = useReduceMotion()
    const night = useIsDarkTheme()
    const { theme } = useUnistyles()
    const [x1, y1, x2, y2] = theme.motion.easing
    const progress = useRef(new Animated.Value(0)).current
    const host = useRef<View>(null)
    // La largeur est un état d'INTERFACE : la page la mesure seule.
    const [width, setWidth] = useState(0)

    useEffect(() => {
        if (reduceMotion) return
        // react-native-web n'a pas de pilote natif ; y prétendre ne fait qu'avertir.
        const useNativeDriver = Platform.OS !== "web"
        const loop = Animated.loop(
            Animated.timing(progress, {
                toValue: 1,
                duration: skeletonSweep.duration,
                easing: Easing.bezier(x1, y1, x2, y2),
                useNativeDriver,
            })
        )
        loop.start()
        return () => {
            loop.stop()
            progress.setValue(0)
        }
    }, [progress, reduceMotion, x1, y1, x2, y2])

    const onLayout = (event: LayoutChangeEvent) => {
        const next = event.nativeEvent.layout.width
        setWidth((previous) => (previous === next ? previous : next))
    }

    const sweep = useMemo<SkeletonSweep>(
        () => ({ progress, host, width, length: sweepLength(width), night, moving: !reduceMotion }),
        [progress, width, night, reduceMotion]
    )

    return (
        <SkeletonSweepContext.Provider value={sweep}>
            <View
                ref={host}
                onLayout={onLayout}
                style={style}
                testID={testID}
                accessible
                accessibilityRole="progressbar"
                accessibilityLabel={accessibilityLabel}
                accessibilityState={{ busy: true }}
            >
                {children}
            </View>
        </SkeletonSweepContext.Provider>
    )
}
